<template>
  <div class="platform-admin-page eh-ds">
    <header class="pa-header">
      <h1 class="pa-title">Platform Administration</h1>
      <p class="pa-lead">
        Customer organizations on the platform. Creating one sets up its default
        groups and invites its first administrator by e-mail.
      </p>
    </header>

    <p v-if="!isOperator" class="pa-refused" role="alert">
      Platform Administration is for platform operators.
    </p>

    <template v-else>
      <section class="pa-section" aria-labelledby="pa-tenants-title">
        <div class="pa-toolbar">
          <h2 id="pa-tenants-title" class="pa-section-title">Organizations</h2>
          <input
            v-model="search"
            type="search"
            class="form-control pa-search"
            placeholder="Search by name or number"
            aria-label="Search organizations"
            @input="onSearchInput"
          />
          <button
            v-if="canManageTenants && !showCreate"
            type="button"
            class="btn btn-primary"
            @click="openCreate"
          >
            New organization
          </button>
        </div>

        <p v-if="notice" class="pa-notice" role="status">{{ notice }}</p>
        <p v-if="loadError" class="pa-error" role="alert">{{ loadError }}</p>
        <p v-if="actionError" class="pa-error" role="alert">{{ actionError }}</p>

        <form v-if="showCreate" class="pa-create" novalidate @submit.prevent="submitCreate">
          <h3 class="pa-create-title">New organization</h3>

          <fieldset class="pa-fieldset">
            <legend>Organization</legend>
            <label class="pa-field">
              <span>Name</span>
              <input v-model.trim="form.organization.name" type="text" class="form-control" name="organization.name" required />
              <small v-if="fieldError('organization.name')" class="pa-field-error">{{ fieldError('organization.name') }}</small>
            </label>
            <label class="pa-field">
              <span>Type</span>
              <select v-model.number="form.organization.type_id" class="form-control" name="organization.type_id" required>
                <option :value="null" disabled>Choose a type</option>
                <option v-for="type in types" :key="type.id" :value="type.id">{{ type.name }}</option>
              </select>
              <small v-if="fieldError('organization.type_id')" class="pa-field-error">{{ fieldError('organization.type_id') }}</small>
            </label>
            <label class="pa-field">
              <span>Phone</span>
              <input v-model.trim="form.organization.phone" type="tel" class="form-control" name="organization.phone" />
            </label>
            <label class="pa-field">
              <span>Address</span>
              <input v-model.trim="form.organization.address_line1" type="text" class="form-control" name="organization.address_line1" />
            </label>
            <div class="pa-row">
              <label class="pa-field">
                <span>City</span>
                <input v-model.trim="form.organization.city" type="text" class="form-control" name="organization.city" />
              </label>
              <label class="pa-field pa-field--narrow">
                <span>State</span>
                <input v-model.trim="form.organization.state" type="text" maxlength="2" class="form-control" name="organization.state" />
                <small v-if="fieldError('organization.state')" class="pa-field-error">{{ fieldError('organization.state') }}</small>
              </label>
              <label class="pa-field pa-field--narrow">
                <span>ZIP</span>
                <input v-model.trim="form.organization.postal_code" type="text" class="form-control" name="organization.postal_code" />
              </label>
            </div>
            <label class="pa-field">
              <span>Organization NPI (optional)</span>
              <input v-model.trim="form.organization.npi" type="text" inputmode="numeric" maxlength="10" class="form-control" name="organization.npi" />
              <small v-if="fieldError('organization.npi')" class="pa-field-error">{{ fieldError('organization.npi') }}</small>
            </label>
          </fieldset>

          <fieldset class="pa-fieldset">
            <legend>First administrator</legend>
            <p class="pa-hint">
              They receive an invitation to choose their own password. Their
              credentials are only needed if they will sign clinical documents.
            </p>
            <div class="pa-row">
              <label class="pa-field">
                <span>First name</span>
                <input v-model.trim="form.administrator.first_name" type="text" class="form-control" name="administrator.first_name" required />
                <small v-if="fieldError('administrator.first_name')" class="pa-field-error">{{ fieldError('administrator.first_name') }}</small>
              </label>
              <label class="pa-field">
                <span>Last name</span>
                <input v-model.trim="form.administrator.last_name" type="text" class="form-control" name="administrator.last_name" required />
                <small v-if="fieldError('administrator.last_name')" class="pa-field-error">{{ fieldError('administrator.last_name') }}</small>
              </label>
            </div>
            <label class="pa-field">
              <span>E-mail</span>
              <input v-model.trim="form.administrator.email" type="email" class="form-control" name="administrator.email" required />
              <small v-if="fieldError('administrator.email')" class="pa-field-error">{{ fieldError('administrator.email') }}</small>
            </label>
            <label class="pa-field">
              <span>Phone (optional)</span>
              <input v-model.trim="form.administrator.phone" type="tel" class="form-control" name="administrator.phone" />
            </label>
            <div class="pa-row">
              <label class="pa-field">
                <span>NPI (optional)</span>
                <input v-model.trim="form.administrator.npi" type="text" inputmode="numeric" maxlength="10" class="form-control" name="administrator.npi" />
                <small v-if="fieldError('administrator.npi')" class="pa-field-error">{{ fieldError('administrator.npi') }}</small>
              </label>
              <label class="pa-field">
                <span>Professional licence (optional)</span>
                <input v-model.trim="form.administrator.professional_license" type="text" class="form-control" name="administrator.professional_license" />
              </label>
              <label class="pa-field pa-field--narrow">
                <span>Licence state</span>
                <input v-model.trim="form.administrator.license_state" type="text" maxlength="2" class="form-control" name="administrator.license_state" />
              </label>
            </div>
          </fieldset>

          <p v-if="createError" class="pa-error" role="alert">{{ createError }}</p>

          <div class="pa-actions">
            <button type="submit" class="btn btn-primary" :disabled="saving">
              {{ saving ? "Creating…" : "Create organization" }}
            </button>
            <button type="button" class="btn btn-secondary" :disabled="saving" @click="closeCreate">Cancel</button>
          </div>
        </form>

        <table class="pa-table">
          <thead>
            <tr>
              <th scope="col">Organization</th>
              <th scope="col">Type</th>
              <th scope="col">Status</th>
              <th scope="col">First administrator</th>
              <th scope="col">Invitation</th>
              <th scope="col">Created</th>
              <th scope="col"><span class="sr-only">Actions</span></th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="!canManageTenants">
              <td colspan="7" class="pa-empty pa-no-access">You do not have access to the organization list.</td>
            </tr>
            <tr v-else-if="loading">
              <td colspan="7" class="pa-empty">Loading…</td>
            </tr>
            <tr v-else-if="organizations.length === 0">
              <td colspan="7" class="pa-empty">No organizations yet.</td>
            </tr>
            <template v-else>
              <tr v-for="org in organizations" :key="org.public_id" :data-organization="org.public_id">
                <td>
                  <div class="pa-org-name">{{ org.name }}</div>
                  <div class="pa-muted">{{ org.number }}</div>
                </td>
                <td>{{ org.type ? org.type.name : "" }}</td>
                <td><span class="pa-badge" :class="`pa-badge--${org.status}`">{{ org.status }}</span></td>
                <td>
                  <template v-if="org.administrator">
                    <div>{{ org.administrator.first_name }} {{ org.administrator.last_name }}</div>
                    <div class="pa-muted">{{ org.administrator.email }}</div>
                  </template>
                  <span v-else class="pa-muted">None</span>
                </td>
                <td class="pa-invitation">{{ invitationLabel(org.administrator) }}</td>
                <td>{{ formatDate(org.created_at) }}</td>
                <td class="pa-row-actions">
                  <button
                    v-if="canManageTenants && awaitsInvitation(org.administrator)"
                    type="button"
                    class="btn btn-link btn-sm pa-resend"
                    :disabled="resending === org.public_id"
                    @click="resend(org)"
                  >
                    Resend invitation
                  </button>
                  <template v-if="canManageTenants">
                    <span v-if="confirming === org.public_id" class="pa-confirm" role="group" :aria-label="`Suspend ${org.name}`">
                      <span class="pa-confirm-text">
                        Suspend {{ org.name }}? Everyone in it is refused on their next request.
                      </span>
                      <button
                        type="button"
                        class="btn btn-danger btn-sm pa-suspend-confirm"
                        :disabled="changing === org.public_id"
                        @click="setStatus(org, 'suspended')"
                      >
                        Suspend
                      </button>
                      <button type="button" class="btn btn-secondary btn-sm pa-suspend-cancel" @click="confirming = null">Cancel</button>
                    </span>
                    <button
                      v-else-if="org.status === 'active'"
                      type="button"
                      class="btn btn-link btn-sm pa-suspend"
                      :disabled="changing === org.public_id"
                      @click="confirming = org.public_id"
                    >
                      Suspend
                    </button>
                    <button
                      v-else
                      type="button"
                      class="btn btn-link btn-sm pa-activate"
                      :disabled="changing === org.public_id"
                      @click="setStatus(org, 'active')"
                    >
                      {{ org.status === "pending" ? "Activate" : "Reactivate" }}
                    </button>
                  </template>
                </td>
              </tr>
            </template>
          </tbody>
        </table>

        <nav v-if="meta && meta.last_page > 1" class="pa-pager" aria-label="Organizations pages">
          <button type="button" class="btn btn-secondary btn-sm" :disabled="page <= 1 || loading" @click="goTo(page - 1)">Previous</button>
          <span class="pa-muted">Page {{ meta.current_page }} of {{ meta.last_page }}</span>
          <button type="button" class="btn btn-secondary btn-sm" :disabled="page >= meta.last_page || loading" @click="goTo(page + 1)">Next</button>
        </nav>
      </section>
    </template>
  </div>
</template>

<script>
import {
  listOrganizations,
  listOrganizationTypes,
  createOrganization,
  resendAdministratorInvitation,
  updateOrganizationById,
} from "@/api2/organizations";
import { useSessionStore } from "@/stores/session";
import { PRIVILEGES } from "@/constants/privileges";

/** The create form's empty state; only filled fields are sent. */
function emptyForm() {
  return {
    organization: {
      name: "", type_id: null, phone: "", address_line1: "", city: "", state: "", postal_code: "", npi: "",
    },
    administrator: {
      first_name: "", last_name: "", email: "", phone: "", npi: "", professional_license: "", license_state: "",
    },
  };
}

/** Drop blank optional fields, so the API's `nullable` rules see absence, not "". */
function compact(section) {
  return Object.fromEntries(Object.entries(section).filter(([, value]) => value !== "" && value !== null));
}

/**
 * Platform Administration (Trello nRhCnuT0): the customer list and tenant
 * onboarding (qt2p8aSU, V66X6yAy), for platform operators.
 *
 * Shown only to an operator of the platform tenant holding `platform:admin`
 * (`/me` says both). That is UX: every call here needs `platform:tenants`,
 * which the API resolves for nobody outside the platform tenant, and the API
 * asserts the operator besides.
 *
 * Onboarding is operator-led. The API invites the first administrator once
 * the tenant has committed; until the authorization server has accepted that
 * invitation the list says it is owed, and the operator can send it again —
 * the tenant's own resend is closed to someone outside the tenant.
 *
 * A tenant's status is the operator's off switch (`PATCH /organizations/{id}`):
 * a suspended tenant's people are refused on their next request and cannot
 * sign in, so Suspend asks first and names the organization. Reactivate puts
 * it back; Activate is the same for a tenant an earlier build left `pending`,
 * a status nothing honours and the API no longer lets anyone set.
 */
export default {
  name: "PlatformAdministrationPage",
  data() {
    return {
      organizations: [],
      meta: null,
      page: 1,
      search: "",
      searchTimer: null,
      loading: false,
      loadError: "",
      types: [],
      showCreate: false,
      form: emptyForm(),
      fieldErrors: {},
      createError: "",
      saving: false,
      resending: null,
      // The organization whose Suspend is awaiting its confirmation, and the
      // one whose status change is in flight.
      confirming: null,
      changing: null,
      notice: "",
      actionError: "",
      // Which `load()` is the latest: an earlier search answered after a
      // later one must not replace its rows (see load()).
      loadSequence: 0,
    };
  },
  computed: {
    isOperator() {
      const session = useSessionStore();
      return Boolean(session.me?.is_platform_operator) && session.can(PRIVILEGES.PLATFORM_ADMIN);
    },
    canManageTenants() {
      return this.isOperator && useSessionStore().can(PRIVILEGES.PLATFORM_TENANTS);
    },
  },
  mounted() {
    if (this.canManageTenants) this.load();
  },
  beforeUnmount() {
    if (this.searchTimer) clearTimeout(this.searchTimer);
  },
  methods: {
    /**
     * Answers can arrive out of order — a search for "b" that is slower than
     * the "birch" typed after it — so only the latest request's answer is
     * shown; an older one is dropped, or the list would show rows that do
     * not match the search box.
     */
    async load() {
      const sequence = ++this.loadSequence;
      this.loading = true;
      this.loadError = "";
      try {
        const params = { page: this.page };
        if (this.search.trim()) params["filter[name]"] = this.search.trim();
        const { items, meta } = await listOrganizations(params);
        if (sequence !== this.loadSequence) return;
        this.organizations = items;
        this.meta = meta;
      } catch (e) {
        if (sequence !== this.loadSequence) return;
        this.organizations = [];
        this.loadError = e?.message || "The organizations could not be loaded.";
      } finally {
        if (sequence === this.loadSequence) this.loading = false;
      }
    },
    onSearchInput() {
      clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(() => {
        this.page = 1;
        this.load();
      }, 300);
    },
    goTo(page) {
      this.page = page;
      this.load();
    },
    async openCreate() {
      this.form = emptyForm();
      this.fieldErrors = {};
      this.createError = "";
      this.notice = "";
      this.showCreate = true;
      if (this.types.length === 0) {
        try {
          this.types = await listOrganizationTypes();
        } catch (e) {
          this.createError = "The organization types could not be loaded.";
        }
      }
    },
    closeCreate() {
      this.showCreate = false;
    },
    fieldError(name) {
      const messages = this.fieldErrors[name];
      return Array.isArray(messages) ? messages[0] : messages || "";
    },
    async submitCreate() {
      this.fieldErrors = {};
      this.createError = "";
      this.saving = true;
      try {
        const created = await createOrganization({
          organization: compact(this.form.organization),
          administrator: compact(this.form.administrator),
        });
        this.showCreate = false;
        this.notice = `${created.name} was created. An invitation is on its way to ${created.administrator?.email || this.form.administrator.email}.`;
        this.page = 1;
        await this.load();
      } catch (e) {
        this.fieldErrors = e?.fieldErrors || {};
        this.createError = Object.keys(this.fieldErrors).length
          ? "Check the highlighted fields."
          : e?.message || "The organization could not be created.";
      } finally {
        this.saving = false;
      }
    },
    awaitsInvitation(administrator) {
      return Boolean(administrator) && ["invited", "expired"].includes(administrator.status);
    },
    invitationLabel(administrator) {
      if (!administrator) return "";
      if (administrator.status === "expired") return "Expired";
      if (administrator.status === "invited") {
        return administrator.invited_at ? `Sent ${this.formatDate(administrator.invited_at)}` : "Not sent yet";
      }
      return "Accepted";
    },
    async resend(org) {
      this.resending = org.public_id;
      this.notice = "";
      this.actionError = "";
      try {
        const result = await resendAdministratorInvitation(org.public_id);
        this.notice = this.resendNotice(result?.status, org.administrator.email);
        await this.load();
      } catch (e) {
        this.actionError = e?.message || "The invitation could not be sent.";
      } finally {
        this.resending = null;
      }
    },
    /** Suspend (after its confirmation) or put back one organization's people. */
    async setStatus(org, status) {
      this.changing = org.public_id;
      this.confirming = null;
      this.notice = "";
      this.actionError = "";
      try {
        await updateOrganizationById(org.public_id, { status });
        this.notice = status === "active"
          ? `${org.name} is active: its people can sign in again.`
          : `${org.name} is suspended: its people are refused on their next request.`;
        await this.load();
      } catch (e) {
        this.actionError = e?.message || "The status could not be changed.";
      } finally {
        this.changing = null;
      }
    },
    /**
     * The API's three answers: `sent`; `already_sent`, when a link mailed to
     * this address in the last two minutes was kept and nothing new went out
     * (UserInvitationService::REUSE_WITHIN_SECONDS); `undelivered`, when no
     * sign-in service is configured.
     */
    resendNotice(status, email) {
      if (status === "undelivered") return "The invitation could not be sent: the sign-in service is not configured.";
      if (status === "already_sent") return `An invitation was sent to ${email} moments ago; that link is still valid, so no new one was sent.`;
      return `The invitation to ${email} was sent again.`;
    },
    formatDate(value) {
      if (!value) return "";
      const date = new Date(value);
      return Number.isNaN(date.getTime()) ? "" : date.toLocaleDateString();
    },
  },
};
</script>

<style scoped lang="scss">
$pa-navy: #173a63;
$pa-border: #e2e8f0;
$pa-muted: #64748b;

.platform-admin-page {
  max-width: 72rem;
  margin: 0 auto;
  padding: 1.5rem 1.25rem 2.5rem;
}

.pa-header {
  margin-bottom: 1.25rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid $pa-border;
}

.pa-title {
  margin: 0 0 0.5rem;
  font-size: 1.65rem;
  font-weight: 700;
  color: $pa-navy;
  letter-spacing: -0.02em;
}

.pa-lead,
.pa-hint,
.pa-muted {
  color: $pa-muted;
}

.pa-lead {
  margin: 0;
  font-size: 0.95rem;
  line-height: 1.5;
}

.pa-toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 1rem;
}

.pa-section-title {
  margin: 0;
  font-size: 1.2rem;
  font-weight: 700;
  color: $pa-navy;
  flex: 1 1 auto;
}

.pa-search {
  max-width: 18rem;
}

.pa-notice {
  padding: 0.6rem 0.8rem;
  border-radius: 6px;
  background: #ecfdf5;
  color: #065f46;
}

.pa-error,
.pa-refused,
.pa-field-error {
  color: #b91c1c;
}

.pa-create {
  margin-bottom: 1.5rem;
  padding: 1rem 1.25rem;
  border: 1px solid $pa-border;
  border-radius: 8px;
}

.pa-create-title {
  margin: 0 0 0.75rem;
  font-size: 1.05rem;
  font-weight: 700;
}

.pa-fieldset {
  margin-bottom: 1rem;

  legend {
    font-size: 0.95rem;
    font-weight: 600;
  }
}

.pa-row {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
}

.pa-field {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  flex: 1 1 12rem;
  margin-bottom: 0.6rem;
}

.pa-field--narrow {
  flex: 0 1 6rem;
}

.pa-actions {
  display: flex;
  gap: 0.65rem;
}

.pa-table {
  width: 100%;
  border-collapse: collapse;

  th,
  td {
    padding: 0.55rem 0.5rem;
    border-bottom: 1px solid $pa-border;
    text-align: left;
    vertical-align: top;
  }
}

.pa-org-name {
  font-weight: 600;
}

.pa-empty {
  text-align: center;
  color: $pa-muted;
}

.pa-badge {
  text-transform: capitalize;
}

.pa-badge--active {
  color: #065f46;
}

.pa-badge--suspended,
.pa-badge--closed {
  color: #b91c1c;
}

.pa-row-actions {
  white-space: nowrap;
}

.pa-confirm {
  display: inline-flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.4rem;
  max-width: 22rem;
  white-space: normal;
}

.pa-confirm-text {
  flex: 1 1 100%;
  font-size: 0.85rem;
  color: #b91c1c;
}

.pa-pager {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.75rem;
  margin-top: 0.75rem;
}
</style>
